/**
 * AgentComposer — the input bar for the AgentWindow.
 *
 * Wraps the drawer's ChatInput so both surfaces share attachments, voice
 * input, specialist/model pickers, and web-search. Adds a status chip row
 * (connection/paused) and a notebook picker; no workflows sidebar exists in
 * the workspace yet, so that flyout slot is hidden.
 */

import { useCallback, useRef, useState } from "react";
import { Loader2, PauseCircle, Square } from "lucide-react";
import { cn } from "@flowstate/utils";
import { Button } from "@/components/ui/button";
import ChatInput from "@/features/ai-agent/components/ChatInput";
import { NotebookPicker } from "@/features/ai-agent/components/chat-sidebar/NotebookPicker";
import type { CortexEngine } from "../engine/useCortexEngine";

interface AgentComposerProps {
  engine: CortexEngine;
  /** A run is paused on `awaiting`/`awaiting_input`. */
  pausedLabel?: string | null;
}

export function AgentComposer({ engine, pausedLabel }: AgentComposerProps) {
  const [input, setInput] = useState("");
  const inputRef = useRef(input);
  inputRef.current = input;

  const isGenerating = engine.isGenerating;

  const handleSendMessage = useCallback(async () => {
    const text = inputRef.current.trim();
    if (!text || isGenerating) return;
    setInput("");
    await engine.send(text);
  }, [engine, isGenerating]);

  const statusChip = (() => {
    if (engine.connectionStatus === "error" || engine.connectionStatus === "disconnected") {
      return { text: "connection lost", cls: "text-destructive" };
    }
    if (isGenerating) return { text: "streaming", cls: "text-primary" };
    if (pausedLabel) return { text: pausedLabel, cls: "text-amber-600" };
    return null;
  })();

  return (
    <div
      className="flex-shrink-0 border-t border-border/20 bg-background/60 backdrop-blur-xl px-4 py-3"
      data-testid="cortex-input-area"
    >
      {(statusChip || isGenerating) && (
        <div className="mb-2 flex items-center gap-2">
          {statusChip && (
            <span className={cn("flex items-center gap-1.5 text-[11px]", statusChip.cls)}>
              {pausedLabel && !isGenerating ? (
                <PauseCircle className="h-3 w-3" />
              ) : (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              {statusChip.text}
            </span>
          )}
          {isGenerating && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[11px] gap-1 ml-auto"
              onClick={engine.stop}
              title="Stop generating"
            >
              <Square className="h-3 w-3" /> Stop
            </Button>
          )}
        </div>
      )}

      <ChatInput
        input={input}
        setInput={setInput}
        handleSendMessage={handleSendMessage}
        isLoading={isGenerating}
        conversationId={engine.sessionId}
        onFileUploaded={engine.handleFileUploaded}
        pendingAttachments={engine.pendingAttachments}
        onWebSearchClick={() => setInput((prev) => `${prev} [search web]`)}
        selectedSpecialist={engine.selectedSpecialist}
        onSpecialistChange={engine.setSelectedSpecialist}
        selectedProvider={engine.selectedProvider}
        onProviderChange={engine.setSelectedProvider}
        selectedModel={engine.selectedModel}
        onModelChange={engine.setSelectedModel}
        reasoningEffort={engine.reasoningEffort}
        onReasoningEffortChange={engine.setReasoningEffort}
        isGeminiDelegated={engine.isGeminiDelegated}
      />

      {/* Toggle row — notebook grounding */}
      <div className="flex items-center justify-between mt-2 gap-1.5 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <NotebookPicker
            selectedNotebookId={engine.selectedNotebookId}
            onSelectNotebook={engine.setSelectedNotebookId}
          />
        </div>
      </div>
    </div>
  );
}
